import { useEffect } from 'react';
import { countAdSlots, calculateAdDensity } from '../sharedHelpers/googlePublisherTag';

const POLLING_INTERVAL_MS = 1000;
const POLLING_TIMEOUT_MS = 10000;
const INITIAL_DELAY_MS = 2000;

/**
 * Push tracking data to dataLayer
 * @param {Object} trackingData - Data to push to dataLayer
 */
const pushToDataLayer = (trackingData) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'adData',
    ...trackingData,
  });
};

/**
 * Hook to track various ad-related metrics.
 * @param {Object} trackingConfig - Configuration object for tracking features
 * @param {Object} trackingConfig.adCount - Configuration for ad count tracking
 * @param {boolean} trackingConfig.adCount.enabled - Enable ad count tracking
 * @param {Array} trackingConfig.adCount.excludedSlots - Slots to exclude from count
 * @param {Object} trackingConfig.adDensity - Configuration for ad density tracking
 * @param {boolean} trackingConfig.adDensity.enabled - Enable ad density tracking
 * @param {Array} trackingConfig.adDensity.excludedSlots - Slots to exclude from density calculation
 * @param {string} trackingConfig.adDensity.mainContentSelector - CSS selector for main content area
 */
export const useAdTracking = (trackingConfig = {}) => {
  const { adCount, adDensity } = trackingConfig;

  // Polling effect to check for GTM readiness and track ad metrics.
  useEffect(() => {
    const isAdCountEnabled = adCount?.enabled;
    const isAdDensityEnabled = adDensity?.enabled;

    if (!isAdCountEnabled && !isAdDensityEnabled) {
      return undefined;
    }

    const adCountExcludedSlots = adCount?.excludedSlots || [];
    const adDensityExcludedSlots = adDensity?.excludedSlots || [];
    const mainContentSelector = adDensity?.mainContentSelector || '.polaris__content';

    let pollIntervalId;
    let pollTimeoutId;

    const collectMetrics = () => {
      const trackingData = {};

      if (isAdCountEnabled) {
        const count = countAdSlots(adCountExcludedSlots);
        if (count !== null) {
          trackingData.adCount = count;
        }
      }

      if (isAdDensityEnabled) {
        const density = calculateAdDensity(adDensityExcludedSlots, mainContentSelector);
        if (density !== null) {
          trackingData.adDensity = density;
        }
      }

      // Only return data if we have at least one metric
      return Object.keys(trackingData).length > 0 ? trackingData : null;
    };

    const startPolling = () => {
      // Initial check before starting interval.
      const initialMetrics = collectMetrics();
      if (initialMetrics !== null) {
        pushToDataLayer(initialMetrics);
        return;
      }

      // Start polling at intervals.
      pollIntervalId = setInterval(() => {
        const metrics = collectMetrics();
        if (metrics !== null) {
          pushToDataLayer(metrics);
          if (pollIntervalId) {
            clearInterval(pollIntervalId);
            pollIntervalId = undefined;
          }
          if (pollTimeoutId) {
            clearTimeout(pollTimeoutId);
            pollTimeoutId = undefined;
          }
        }
      }, POLLING_INTERVAL_MS);

      // Set timeout to stop polling after a certain period.
      pollTimeoutId = setTimeout(() => {
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
          pollIntervalId = undefined;
        }
      }, POLLING_TIMEOUT_MS);
    };

    const initialDelayId = setTimeout(startPolling, INITIAL_DELAY_MS);

    return () => {
      // Clear timeouts on unmount.
      if (initialDelayId) clearTimeout(initialDelayId);
      if (pollIntervalId) clearInterval(pollIntervalId);
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
    };
  }, []);
};
